import { useEffect, useMemo, useState } from "react";
import { Flex } from "@chakra-ui/react";
import { getFragmentData, gql } from "__generated__/gql";
import { SkuFilter } from "__generated__/gql/graphql";
import ProductAttributesBulkEditModal from "components/Products/ProductAttributesMatrix/BulkEditModal";
import ProductAttributesMatrixTable from "components/Products/ProductAttributesMatrix/MatrixTable";
import filterDefinitions from "components/Products/filterDefinitions";
import FilterBarWithSearch from "components/Shared/FilterEditor/FilterBar/FilterBarWithSearch";
import { useGraphileFiltersUrlQuery } from "components/Shared/FilterEditor/urlQuerySerializers";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { usePrepApolloData } from "components/Shared/ChakraReactTable/utils";
import { useOptionalCombineFilters } from "utils/useCombineFilters";
import EditButton from "components/Shared/EditButton";

const skuProductAttributeIdsFragment = gql(/* GraphQL */ `
  fragment ProductAttributesMatrixSkuProductAttributeIds on Sku {
    id
    productAttributesList(
      filter: { isArchived: { equalTo: false } }
      orderBy: NAME_ASC
    ) {
      id
    }
  }
`);

const query = gql(/* GraphQL */ `
  query ProductAttributesMatrix($skuFilter: SkuFilter!) {
    attributesList(
      filter: {
        isArchived: { equalTo: false }
        attributeType: { equalTo: PRODUCT }
      }
      orderBy: NAME_ASC
    ) {
      id
      ...ProductAttributesMatrixTableAttribute
      ...ProductAttributesMatrixBulkModalAttribute
    }
    skusList(filter: $skuFilter, orderBy: [FULL_NAME_ASC]) {
      id
      ...ProductAttributesMatrixTableSku
      ...ProductAttributesMatrixBulkModalSku
      ...ProductAttributesMatrixSkuProductAttributeIds
    }
  }
`);

export default function ProductAttributesMatrix() {
  const brandId = useCurrentBrandId();
  const { filter, filters, isTableReady, handleFiltersChange } =
    useGraphileFiltersUrlQuery<SkuFilter>(filterDefinitions);

  const [isEditMode, setIsEditMode] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [selectedSkuIds, setSelectedSkuIds] = useState<UUID[]>([]);

  const baseSkuFilter = useMemo<SkuFilter | undefined>(
    () =>
      brandId
        ? {
            isArchived: { equalTo: false },
            productLine: { brandId: { equalTo: brandId } },
          }
        : undefined,
    [brandId],
  );
  const skuFilter = useOptionalCombineFilters(baseSkuFilter, filter);

  const { data, loading, refetch } = useBrandQuery(query, {
    skip: !isTableReady || !skuFilter,
    variables: skuFilter ? { skuFilter } : undefined,
    fetchPolicy: "cache-and-network",
  });

  const isLoading = loading || typeof window === "undefined";

  const skuRows = useMemo(() => data?.skusList || [], [data?.skusList]);
  const attributeRows = useMemo(
    () => data?.attributesList || [],
    [data?.attributesList],
  );

  const editableSkus = usePrepApolloData(
    getFragmentData(skuProductAttributeIdsFragment, skuRows),
  );
  const skuAttributeIdsBySkuId = useMemo(
    () =>
      editableSkus.reduce<Record<string, readonly UUID[]>>((acc, sku) => {
        acc[sku.id] = sku.productAttributesList.map(
          (attribute) => attribute.id,
        );
        return acc;
      }, {}),
    [editableSkus],
  );

  const selectedSkuRows = useMemo(
    () => skuRows.filter((sku) => selectedSkuIds.includes(sku.id)),
    [selectedSkuIds, skuRows],
  );
  const selectedSkuAttributeIdsBySkuId = useMemo(
    () =>
      selectedSkuIds.reduce<Record<string, readonly UUID[]>>((acc, skuId) => {
        acc[skuId] = skuAttributeIdsBySkuId[skuId] || [];
        return acc;
      }, {}),
    [selectedSkuIds, skuAttributeIdsBySkuId],
  );

  useEffect(() => {
    const availableSkuIds = editableSkus.map((sku) => sku.id);

    setSelectedSkuIds((existing) =>
      existing.filter((skuId) => availableSkuIds.includes(skuId)),
    );
  }, [editableSkus]);

  useEffect(() => {
    if (isEditMode) return;
    setSelectedSkuIds([]);
    setIsBulkModalOpen(false);
  }, [isEditMode]);

  useEffect(() => {
    if (selectedSkuIds.length >= 2) return;
    setIsBulkModalOpen(false);
  }, [selectedSkuIds.length]);

  return (
    <Flex direction="column" flex="1 1 100%" minH={0}>
      <FilterBarWithSearch
        key={String(isTableReady)}
        filterDefinitions={filterDefinitions}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        headerRight={
          <Flex pr={4} gap={2} alignItems="center">
            {isEditMode && (
              <EditButton
                isDisabled={selectedSkuIds.length < 2}
                onClick={() => setIsBulkModalOpen(true)}
              >
                Edit Multiple…
              </EditButton>
            )}
            <EditButton
              size="xs"
              onClick={() => setIsEditMode((existing) => !existing)}
              isEditing={isEditMode}
              variant={isEditMode ? "solid" : "outline"}
            >
              {isEditMode ? "Done" : "Edit"}
            </EditButton>
          </Flex>
        }
      />

      <ProductAttributesMatrixTable
        attributes={attributeRows}
        skus={skuRows}
        isLoading={isLoading}
        isEditMode={isEditMode}
        onRefresh={refetch}
        selectedSkuIds={selectedSkuIds}
        skuAttributeIdsBySkuId={skuAttributeIdsBySkuId}
        onSelectedSkuIdsChange={setSelectedSkuIds}
      />

      <ProductAttributesBulkEditModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        onComplete={() => {
          setIsBulkModalOpen(false);
          setSelectedSkuIds([]);
        }}
        attributes={attributeRows}
        selectedSkus={selectedSkuRows}
        selectedSkuAttributeIdsBySkuId={selectedSkuAttributeIdsBySkuId}
      />
    </Flex>
  );
}
